/**
 * Buyer Kickback Gifts page
 * --------------------------
 * Endpoints:
 *   GET /api/buyer/gifts
 *   GET /api/buyer/gifts/{id}
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, X, Clock, CheckCircle, Wallet, XCircle } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, boxShadow: theme.colors.shadows.sm,
};

const STATUS_META = {
  PENDING: { color: theme.colors.status.warning, Icon: Clock },
  APPROVED: { color: theme.colors.accent.teal, Icon: CheckCircle },
  PAID: { color: theme.colors.status.success, Icon: Wallet },
  FAILED: { color: theme.colors.status.error, Icon: XCircle },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || { color: theme.colors.text.muted, Icon: Clock };
  const Icon = meta.Icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999,
      background: `${meta.color}1A`, color: meta.color,
      fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
    }}>
      <Icon size={12} /> {status || '—'}
    </span>
  );
};

export default function BuyerGiftsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/buyer/gifts`, auth);
        if (cancelled) return;
        setRows(res.data?.gifts || []);
      } catch (e) {
        if (!cancelled) toast.error('Failed to load gifts');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [auth]);

  return (
    <div data-testid="buyer-gifts-page" style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 800, color: theme.colors.text.primary, margin: 0,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <Gift size={26} color={theme.colors.primary} />
          Kickback Gifts
        </h1>
        <p style={{ color: theme.colors.text.secondary, marginTop: 4 }}>
          Gifts released to you from portfolios where you hold NFTs.
        </p>
      </div>

      {loading ? (
        <div data-testid="gifts-loading" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.muted }}>
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="gifts-empty" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.secondary }}>
          You have not received any kickback gifts yet.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {rows.map((g) => (
            <div
              key={g.id}
              data-testid={`gift-row-${g.id}`}
              onClick={() => setDrawer(g)}
              style={{ ...cardStyle, padding: 18, cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 700, color: theme.colors.text.primary, fontSize: 16, marginBottom: 4 }}>
                    {g.portfolio_name}
                  </div>
                  <div style={{ fontSize: 12, color: theme.colors.text.muted }}>
                    {g.units} unit(s) · {fmtDate(g.created_at)}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: theme.colors.primary }}>
                    ${Number(g.amount_usd).toFixed(2)} <span style={{ fontSize: 13, color: theme.colors.text.muted }}>{g.currency}</span>
                  </div>
                  <div style={{ marginTop: 4 }}><StatusPill status={g.status} /></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {drawer && (
        <div data-testid="gift-drawer" style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', justifyContent: 'flex-end', zIndex: 200,
        }} onClick={() => setDrawer(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width: 'min(520px, 100%)', height: '100%', background: theme.colors.bg.card,
            padding: 24, overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ fontWeight: 800, fontSize: 18 }}>Gift Detail</div>
              <button onClick={() => setDrawer(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            {[
              ['Portfolio', drawer.portfolio_name],
              ['Amount', `$${Number(drawer.amount_usd).toFixed(2)} ${drawer.currency}`],
              ['Units', drawer.units],
              ['Status', drawer.status],
              ['Created', fmtDate(drawer.created_at)],
              ['Approved', fmtDate(drawer.approved_at)],
              ['Paid', fmtDate(drawer.paid_at)],
              ['Notes', drawer.notes],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${theme.colors.borders}`, fontSize: 13 }}>
                <span style={{ color: theme.colors.text.muted }}>{k}</span>
                <span style={{ fontWeight: 600 }}>{v || '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
